import React, { useMemo } from "react";

import { Skeleton } from "@/components/ui/skeleton";

import AddressOverviewCard from "./AddressOverviewCard";
import CurrentTenancyCard from "./CurrentTenancyCard";
import DepositCard from "./DepositCard";
import FinanceCard from "./FinanceCard";
import MoveInDocumentsCard from "./MoveInDocumentsCard";
import OpenTasksCard from "./OpenTasksCard";
import ProfileContactCard from "./ProfileContactCard";
import {
  selectCurrentAddress,
  selectCurrentTenancy,
  selectDepositView,
  selectLastActivityAt,
  selectMoveInProtocol,
} from "./overviewSelectors";

/**
 * The tenant-detail "Übersicht" dashboard (Slice O1a).
 *
 * PRESENTATION + DERIVATION ONLY. Every value is derived from data the page has
 * already fetched; this component owns no request and no mutation. That is what
 * keeps the page an orchestrator (Regel 48) and this panel testable.
 *
 * THEME. The panel paints its own ground from the semantic `--background`
 * token, so it is light in light mode — the design target — and dark in dark
 * mode. The surrounding page shell writes literal dark hex, but `index.css`
 * (:296ff) carries a light-mode compatibility layer that remaps exactly those
 * classes, so shell and panel resolve to the same near-identical warm neutral in
 * both themes; browser QA confirmed there is no seam. Tokens are still the right
 * choice here: they need no entry in that layer.
 */
export default function TenantOverviewPanel({
  tenant,
  permitLabel,
  loading = false,
  loadError = null,

  addresses,
  addressesLoading = false,
  addressesError = null,
  addressHistoryOpen = false,
  onToggleAddressHistory,
  addressSection = null,

  tenancies,
  // ALREADY RESOLVED, not raw rows (TINV-2). The invoice world a tenant's
  // money lives in depends on the organization's `invoice_creation_provider`,
  // which is a server answer this presentation-only panel must not fetch and
  // must not guess — and under a Bexio provider there is no native row array to
  // hand down at all. `useTenantInvoiceOverview` performs the one gated read and
  // applies `selectInvoiceOverview` for the Vantio world; what arrives here is
  // its verdict.
  invoiceOverview,
  inspectionsList,
  tenantDocumentCount,
  historyEvents,
  tasks,
  tasksError = null,

  onEditProfile,
  onGoToTab,
}) {
  const currentTenancy = useMemo(
    () => selectCurrentTenancy(tenancies),
    [tenancies]
  );
  const currentAddress = useMemo(
    () => selectCurrentAddress(addresses),
    [addresses]
  );
  const deposit = useMemo(
    () => selectDepositView(currentTenancy),
    [currentTenancy]
  );
  const protocol = useMemo(
    () => selectMoveInProtocol(inspectionsList, currentTenancy?.id),
    [inspectionsList, currentTenancy]
  );
  const lastActivityAt = useMemo(
    () => selectLastActivityAt(historyEvents),
    [historyEvents]
  );

  const goTo = (tabId) => () => {
    if (typeof onGoToTab === "function") onGoToTab(tabId);
  };

  if (loading) {
    return (
      <div
        className="rounded-2xl bg-background p-4 sm:p-5"
        data-testid="overview-loading"
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,380px)] lg:items-start">
          <div className="flex flex-col gap-4">
            <Skeleton className="h-[168px] w-full rounded-xl" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              <Skeleton className="h-[212px] w-full rounded-xl" />
              <Skeleton className="h-[212px] w-full rounded-xl" />
            </div>
          </div>
          <Skeleton className="h-[300px] w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-2xl bg-background p-4 sm:p-5">
        <div
          className="rounded-xl border border-destructive/30 bg-destructive/5 p-5"
          role="alert"
          data-testid="overview-error"
        >
          <p className="m-0 text-[13.5px] font-medium text-destructive">
            Übersicht konnte nicht geladen werden.
          </p>
          <p className="m-0 mt-1 text-[12.5px] text-muted-foreground">
            {loadError}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl bg-background p-4 sm:p-5"
      data-testid="tenant-overview-panel"
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,380px)] lg:items-start">
        {/* Left column — the working surface. */}
        <div className="flex min-w-0 flex-col gap-4">
          <ProfileContactCard
            tenant={tenant}
            permitLabel={permitLabel}
            onEdit={onEditProfile}
          />

          {/*
            Two cards side by side wherever they actually fit. `md` is the full
            page width in single-column mode (~370px each at 768px); at `lg` the
            left column narrows to 2fr of the split, where two cards would be
            ~300px and the CHF figures start wrapping — so it drops back to one
            and returns at `xl`, where 2fr is wide enough again.
          */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <CurrentTenancyCard
              tenancy={currentTenancy}
              onOpen={goTo("tenancies")}
            />
            {/* K4d: read-only. Editing a deposit means editing ONE tenancy's
                deposit, and this card can only show one of possibly several —
                so both actions live on the individual tenancy cards now. */}
            <DepositCard deposit={deposit} />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <FinanceCard invoices={invoiceOverview} onOpen={goTo("invoices")} />
            <MoveInDocumentsCard
              protocol={protocol}
              documentCount={tenantDocumentCount}
              hasTenancy={Boolean(currentTenancy)}
              onOpen={goTo("documents")}
            />
          </div>

          <OpenTasksCard
            tasks={tasks}
            error={tasksError}
            onOpen={goTo("tasks")}
          />
        </div>

        {/* Right column — who and where. */}
        <div
          className="flex min-w-0 flex-col gap-3"
          data-testid="overview-address-column"
        >
          <AddressOverviewCard
            tenant={tenant}
            address={currentAddress}
            addressesLoading={addressesLoading}
            addressesError={addressesError}
            tenancyCount={Array.isArray(tenancies) ? tenancies.length : 0}
            lastActivityAt={lastActivityAt}
            historyOpen={addressHistoryOpen}
            onToggleHistory={onToggleAddressHistory}
          />

          {/*
            The full address book — current / former / other addresses,
            "Adresswechsel erfassen", add, edit, remove — kept intact behind the
            card's action. It opens directly under that card, in the same
            column: rendered below the whole grid it landed under the far
            taller left column, out of sight of the toggle that opened it. The
            tighter gap than the grid's keeps it visibly attached to the card.
            In the one-column layout this column follows the left one, so the
            order there is card, then history — as before.
          */}
          {addressHistoryOpen && addressSection ? (
            <section
              className="overflow-hidden rounded-xl border border-border bg-card px-5 py-4 shadow-sm"
              data-testid="overview-address-history"
            >
              {/* No heading of our own: TenantAddressesSection already titles
                  itself ("Adressen") and each of its three groups. A wrapper
                  title here read as the same word twice, one above the other. */}
              {addressSection}
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
